import BulkSensorUploader from '@Components/BulkSensorUploader/BulkSensorUploader';
import { ActionButton, CancelButton } from '@Components/Buttons';
import { PageWrapper } from '@innovyze/stylovyze';
import { getAssetUploadURL, getSensorUploadURL } from '@Services/sensors';
import { useGlobalization } from '@Translations';
import { MAIN_ROUTES } from '@Types/routes';
import {
	UploadFileStatusType,
	UPLOAD_FILE_STATUS_TEXT,
} from '@Types/sensor.types';
import { addParam } from '@Utils/actions';
import React from 'react';
import { useHistory } from 'react-router-dom';

interface PresignedFileUploadResponse {
	file: File;
	processId: string;
	key: string;
	url: string;
}

const BulkSensor = () => {
	const { t } = useGlobalization();
	const history = useHistory();
	const uploadToS3 = async function (
		url: string,
		uploadFile: File,
		fname: string,
	): Promise<void> {
		const csvFile = new File([uploadFile], fname, { type: 'text/csv' });
		return fetch(url, {
			method: 'PUT',
			body: csvFile, // Note: file 'type' field is needed for proper signature generation on Windows
		}).then(res => {
			if (res.status == 200) {
				console.log('successfully uploaded config to s3');
			} else {
				throw new Error('error uploading config to s3!');
			}
		});
	};

	const [createBulkSensorDisabled, setCreateBulkSensorDisabled] =
		React.useState(true);
	const initialCreateButtonText = t('Create Sensor');
	const loadingCreateButtonText = t('Uploading');
	const retryCreateButtonText = t('Retry');
	const [createButtonText, setCreateButtonText] = React.useState(
		initialCreateButtonText,
	);

	const [uploadStatus, setUploadStatus] =
		React.useState<UploadFileStatusType>('initial');
	const [filesReadyForUpload, setFilesReadyForUpload] =
		React.useState<PresignedFileUploadResponse[]>();
	const [originalFiles, setOriginalFiles] = React.useState<File[]>();
	const [isRetry, setIsRetry] = React.useState(false);

	const startS3Upload = function (
		fileRequests: PresignedFileUploadResponse[],
	) {
		setUploadStatus('preparing');
		setCreateBulkSensorDisabled(true);
		setCreateButtonText(loadingCreateButtonText);
		const s3uploads = [];
		if (fileRequests) {
			for (const fileReadyForUpload of fileRequests) {
				s3uploads.push(
					uploadToS3(
						fileReadyForUpload.url,
						fileReadyForUpload.file,
						fileReadyForUpload.key,
					),
				);
			}
			Promise.all(s3uploads).then(
				() => {
					console.log('all configs have been uploaded');
					setUploadStatus('completed');
					setCreateButtonText(initialCreateButtonText);
					setTimeout(() => {
						history.push(
							addParam({
								name: 'tab',
								value: '2',
								tab: 'sensor-configuration',
							}),
						);
					}, 5000);
				},
				() => {
					setUploadStatus('error');
					setCreateButtonText(retryCreateButtonText);
					setIsRetry(true);
					setCreateBulkSensorDisabled(false);
					console.log('error uploading files to S3');
				},
			);
		}
	};

	const prepareUploads = function (files: File[]) {
		const presignedFileUploadRequests: PresignedFileUploadResponse[] = [];
		const presignRequests = [];
		setUploadStatus('preparing');
		for (const file of files) {
			presignRequests.push(getSensorUploadURL(file.name));
			presignRequests.push(getAssetUploadURL(file.name));
		}
		Promise.all(presignRequests).then(
			responses => {
				for (const response of responses) {
					const fileName = JSON.parse(response.config.data).fileName;
					const uploadedFile = files.find(
						file => file.name == fileName,
					);
					if (uploadedFile) {
						const presignedFileUploadRequest: PresignedFileUploadResponse =
							{
								file: uploadedFile,
								processId: response.data.processId,
								url: response.data.url,
								key: response.data.key,
							};
						presignedFileUploadRequests.push(
							presignedFileUploadRequest,
						);
					}
				}
				setFilesReadyForUpload(presignedFileUploadRequests);
				setUploadStatus('ready');
				setCreateBulkSensorDisabled(false);
				console.log('Successfully retrieved all presign URLs');
				if (isRetry) {
					startS3Upload(presignedFileUploadRequests);
				}
			},
			() => {
				setUploadStatus('error');
				setCreateBulkSensorDisabled(false);
				setIsRetry(true);
				setCreateButtonText(retryCreateButtonText);
				console.log('error retrieving presign URLs');
			},
		);
	};

	const onReadyForUpload = function (files: File[]) {
		setOriginalFiles(files);
		prepareUploads(files);
	};

	const secondaryButtons = [
		// CancelBulkSensorButton
		<CancelButton
			key={'CreateBulkSensorButton'}
			data-cy="cancel"
			onClick={() => history.push(MAIN_ROUTES.SensorConfiguration)}>
			{t('Cancel')}
		</CancelButton>,
		// CreateBulkSensorButton
		<ActionButton
			key={'CreateBulkSensorButton'}
			data-cy="createSensor"
			color="primary"
			disabled={createBulkSensorDisabled}
			onClick={() => {
				if (isRetry && originalFiles) prepareUploads(originalFiles);
				else if (filesReadyForUpload) {
					startS3Upload(filesReadyForUpload);
				}
			}}>
			{createButtonText}
		</ActionButton>,
	];

	return (
		<PageWrapper
			title={t('Bulk Sensor Creation')}
			secondary={secondaryButtons}
			headerUnderline={true}
			applyPadding={true}>
			<BulkSensorUploader
				statusText={UPLOAD_FILE_STATUS_TEXT(t)[uploadStatus]}
				onReadyForUpload={onReadyForUpload}
			/>
		</PageWrapper>
	);
};

export default BulkSensor;
